import { useCallback, useEffect, useState } from "react";
import { Box, Card, CardContent, Typography, Chip, Divider } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import api from "./../api";
import { MONO, tokens } from "./../theme";
import { PageHeader, Eyebrow, StatusDot } from "./../components/ui";
import JobDetailModal from "./../components/JobDetailModal";

const STATUS = {
  running: { color: tokens.signal, label: "running" },
  pending: { color: tokens.slate, label: "pending" },
  error: { color: tokens.crit, label: "error" },
  canceled: { color: tokens.slate, label: "canceled" },
  done: { color: tokens.ok, label: "done" },
};

function JobRow({ job, position, onClick }) {
  const s = STATUS[job.status] || STATUS.pending;
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        py: 1.1,
        px: 0.5,
        borderBottom: `1px solid ${tokens.line}`,
        "&:last-child": { borderBottom: 0 },
        ...(onClick && {
          cursor: "pointer",
          borderRadius: 1.5,
          "&:hover": { background: "#f7fafd" },
        }),
      }}
    >
      {position != null ? (
        <Typography sx={{ fontFamily: MONO, fontSize: ".8rem", color: "#9aa8bf", width: 26, flexShrink: 0 }}>
          {String(position).padStart(2, "0")}
        </Typography>
      ) : (
        <StatusDot color={s.color} pulse={job.status === "running"} size={8} />
      )}
      <Typography sx={{ fontFamily: MONO, fontSize: ".85rem", fontWeight: 600, minWidth: 0, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {job.generator}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
        {job.media_type === "classify" && (
          <Chip label="classify" size="small" variant="outlined"
            sx={{ height: 20, fontSize: ".68rem", color: tokens.signal, borderColor: `${tokens.signal}55` }} />
        )}
        {job.gpu_index != null && (
          <Typography sx={{ fontFamily: MONO, fontSize: ".74rem", color: "text.secondary" }}>GPU {job.gpu_index}</Typography>
        )}
        {job.vram_mb ? (
          <Typography sx={{ fontFamily: MONO, fontSize: ".74rem", color: "text.secondary" }}>{Math.round(job.vram_mb).toLocaleString()}MB</Typography>
        ) : null}
        <Chip
          label={s.label}
          size="small"
          sx={{ bgcolor: `${s.color}14`, color: s.color, minWidth: 68 }}
        />
      </Box>
      {job.error && (
        <Typography sx={{ fontFamily: MONO, fontSize: ".72rem", color: tokens.crit, maxWidth: 240, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {job.error}
        </Typography>
      )}
      {onClick && <ChevronRightIcon sx={{ fontSize: 18, color: "#9aa8bf", flexShrink: 0 }} />}
    </Box>
  );
}

function Section({ title, count, hint, children, empty }) {
  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1 }}>
          <Eyebrow>{title}</Eyebrow>
          <Typography sx={{ fontFamily: MONO, fontSize: ".78rem", color: "text.secondary" }}>· {count}</Typography>
          {hint && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
              {hint}
            </Typography>
          )}
        </Box>
        <Divider sx={{ borderColor: tokens.line, mb: 0.5 }} />
        {count ? children : <Typography sx={{ fontFamily: MONO, fontSize: ".8rem", color: "#9aa8bf", py: 1 }}>{empty}</Typography>}
      </CardContent>
    </Card>
  );
}

export default function Queue() {
  const [q, setQ] = useState({ pending: [], running: [], history: [] });
  const [detail, setDetail] = useState(null); // a run opened for its full details

  const load = useCallback(() => api.get("/api/queue", { silent: true }).then(setQ).catch(() => {}), []);
  useEffect(() => {
    load();
    const t = setInterval(load, 2000);
    return () => clearInterval(t);
  }, [load]);

  const running = q.running || [];
  const pending = q.pending || [];
  const history = q.history || [];

  return (
    <Box>
      <PageHeader
        eyebrow="Monitor"
        title="Queue"
        subtitle="Jobs are admitted by the VRAM-packing scheduler and run in submission order per GPU."
      />

      <Section title="Running" count={running.length} empty="Nothing running right now.">
        {running.map((j) => (
          <JobRow key={j.id} job={j} />
        ))}
      </Section>

      <Section title="Pending" count={pending.length} hint="in submission order" empty="The queue is clear.">
        {pending.map((j, i) => (
          <JobRow key={j.id} job={j} position={i + 1} />
        ))}
      </Section>

      <Section title="Recent" count={history.length} hint="click a run for full details" empty="No finished jobs yet.">
        {history.map((j) => (
          <JobRow key={j.id} job={j} onClick={() => setDetail(j)} />
        ))}
      </Section>

      {detail && <JobDetailModal job={detail} onClose={() => setDetail(null)} />}
    </Box>
  );
}
